import isPlainObject from 'lodash/isPlainObject';
import {
  AutomationUsedRequestBody,
  publishAutomationUsed,
} from '@elation/api-client/src/product-metrics';

export interface ProddevSemanticConventions {
  /**
   * The name of the feature in the application
   *
   * e.g. "Note Assist", "Medication Reconciliation"
   */
  'proddev.feature.name'?: string;
  /**
   * Indicates that this event represents the moment that the feature
   * has delivered its value for the user. Used specifically so that we
   * can create dashboards reflecting the level of adoption and usage
   * across all Elation features.
   *
   * This attribute is necessary for the aforementioned use-case because
   * a single feature may have many events that are associated with it.
   * A naive count of all the instances of these events does NOT reveal
   * the level of usage of the feature.
   *
   * e.g. Clicking a tracked button inside of the Annotations Dialog
   * toolbar 100x and then hitting save does not constitute 100 uses of
   * the feature. It constitutes 1 use.
   */
  'proddev.feature.value_moment'?: boolean;
  /**
   * The name of the Elation team that owns the feature represented by
   * `proddev.feature.name` and more specifically the event that this
   * attribute is applied to.
   *
   * **NOTE**: If your team name is not represented, please add it to the Type
   * Definition.
   *
   */
  'proddev.team.name'?:
    | 'aif'
    | 'ce'
    | 'fope'
    | 'gpt'
    | 'thor'
    | 'orders'
    | 'aces'
    | 'stitch'
    | 'ee';
  /**
   * Indicates that this event represents usage of a “Automation”. This
   * attribute allows us to collect the Proddev "Automation Used" metrics.
   *
   * An "Automation" is a feature that reduces, deflects or eliminates
   * burdensome tasks for the practice and saves them time.
   *
   * Providing this attribute SHOULD also include `proddev.automation.name`,
   * `proddev.team.name`, and `proddev.automation.estimated_time_savings`
   */
  'proddev.automation.used'?: boolean;
  /**
   * A number representing the estimated amount of time that the automation
   * saved, in seconds
   *
   * @unit 'seconds'
   */
  'proddev.automation.estimated_time_saved_secs'?: number;
  /**
   * A unique name to represent the automation. If none is provided
   * `product.feature.name` will be used instead for "Automation Used"
   * metric purposes.
   */
  'proddev.automation.name'?: string;
}

// Consumers can genuinely publish anything here
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface EventData extends Record<string, any>, ProddevSemanticConventions {}

/**
 * Routes product analytics event data to the "Automation Used"
 * product metrics endpoint when attributes are encountered in event
 * data that match the proddev.automation semantic conventions
 */
async function routeToAutomationUsedEndpoint(
  _eventName: string,
  eventData: EventData,
): Promise<void> {
  if (eventData['proddev.automation.used']) {
    const automationName =
      eventData['proddev.automation.name'] ?? eventData['proddev.feature.name'];
    if (!automationName) {
      console.warn(
        `When using 'proddev.automation.used' either 'proddev.automation.name' or 'proddev.feature.name' MUST be provided`,
      );
      return;
    }

    const teamName = eventData['proddev.team.name'];
    if (!teamName) {
      console.warn(`When using 'proddev.automation.used' a 'proddev.team.name' MUST be provided`);
      return;
    }

    const payload: AutomationUsedRequestBody = {
      metric_name: automationName,
      team_name: teamName,
      estimated_time_saved_secs: eventData['proddev.automation.estimated_time_saved_secs'],
    };

    const practiceId = elate.data.currPractice.id ?? el8Globals.PRACTICE_ID;

    await publishAutomationUsed(practiceId, payload);
  }
}

export const logEventViaLegacy = (eventName: string, eventData: EventData = {}): void => {
  /**
   * This method is a necessary evil to get around how we currently build our amplitude event properties
   * logEvent below only works as expected ( getting all user properties ) if a legacy amplitude event gets called which
   * will set up a properly speced amplitude instance. If you go directly to log an event using react a nearly blank
   * instance of amplitude gets used
   *
   * @see static/js/legacy/el8/core/util/amplitude/amplitudeHelpers.js
   * @see el8.util.amplitude.initialize
   *
   * DO NOT use this outside static/js/react/apps/emr/src/views/onboarding/amplitudeTaxonomy.ts
   */
  try {
    // @ts-ignore
    if (el8.util.amplitude) {
      // @ts-ignore
      el8.util.amplitude.logEvent(eventName, eventData);
    }
  } catch (err) {
    // not going to do anything if it fails
  }
};

/**
 * Logs a User Action Event to our Product Analytics tools (Datadog & Amplitude)
 * @param eventName Human readable event name or title in Amplitude/Datadog
 * @param eventData
 * @param highPriority When set to false, this value indicates that the event MAY be
 * dropped from Amplitude for Event Quota purposes. Defaults to true.
 */
export const logEvent = (
  eventName: string,
  eventData: EventData = {},
  highPriority = true,
): void => {
  try {
    if (window.amplitude) {
      const instance = window.amplitude.getInstance();
      let amplitudeEventId;

      if (highPriority) {
        amplitudeEventId = instance.logEvent(eventName, eventData);
      }

      // Forwarding Amplitude Events to DataDog RUM gives us a way
      // to beef up the Event Timeline in Session Replays (as we use
      // DataDog RUM for replays at the moment).
      window.DD_RUM?.addAction(`Amplitude:${eventName} `, {
        type: 'amplitude',
        pageTiming: performance.now(),
        eventData,
        amplitudeEventId,
        amplitudeSessionId: instance.getSessionId(),
      });

      // Forward Amplitude events to Userpilot for product analytics
      // and user behavior tracking
      window.userpilot?.track(eventName, eventData);

      // Forward product analytics events to the OpenTelemetry Collector
      // for observability pipelines
      window.logProductAnalyticsEvent?.(eventName, eventData);

      routeToAutomationUsedEndpoint(eventName, eventData);
    }
  } catch (err) {
    // not going to do anything if it fails
  }
};

/**
 * Helper function to update multiple user props.
 * Uses amplitude.Identify().set() which will overwrite existing properties
 * Use addUserProperty() if you want to increment on a user property!
 * @param {Object} userProps - object with key/values that we want to update
 * on user
 */
export const setUserProperties = (userProps: { [key: string]: EventData }): void => {
  try {
    if (!isPlainObject(userProps)) {
      throw new Error(`Incorrect user props. Expected a plain object`);
    }
    if (window.amplitude) {
      const identify = Object.keys(userProps).reduce(
        (resultIdentity, key) => resultIdentity.set(key, userProps[key]),
        new window.amplitude.Identify(),
      );
      window.amplitude.getInstance().identify(identify);
    }
  } catch (err) {
    // not going to do anything if it fails
  }
};

/**
 * Helper function to increment user property.
 * Use setUserProperties() if you want to set user properties!
 * @param propertyName - key that we want to update
 * @param amount - value that we want to increment by
 * on user
 */
export const addUserProperty = (propertyName: string, amount: number): void => {
  try {
    if (window.amplitude) {
      const identify = new window.amplitude.Identify().add(propertyName, amount);
      window.amplitude.getInstance().identify(identify);
    }
  } catch (err) {
    // not going to do anything if it fails
  }
};

/**
 * Helper function to update group properties. Group props are not supported
 * by the JS SDK, so we are hitting their API using fetch
 * @param groupType - Group type eg. 'practice' or 'enterprise'
 * @param groupValue - Identifier for the specific group eg. the practice's or enterprise's id
 * @param groupProps - object with key/values that we want to update eg. {color: 'blue'}

 */
export const updateGroup = (
  groupType: string,
  groupValue: string | number,
  // el8-migrate(rm-31221): DO NOT COPY AND PASTE THESE LINES TO SILENCE YOUR OWN LINTING ERRORS
  // This comment is here solely to flag all of the existing `no-explicit-any` offenders
  // when the rule was set to "error"
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  groupProps: { [key: string]: any },
): void => {
  try {
    const url = 'https://api.amplitude.com/groupidentify';
    const apiKey = 'a59032e26e455d570adefcdd6cc5ca6e';
    const identification = {
      group_properties: groupProps,
      group_value: groupValue,
      group_type: groupType,
    };

    fetch(url, {
      method: 'POST',
      body: `api_key=${apiKey}&identification=${JSON.stringify(identification)}`,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  } catch (err) {
    // don't do anything if it fails
  }
};
